import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FiList } from 'react-icons/fi';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import BreadcrumbBack from '../components/BreadcrumbBack';
import MobilePageBar from '../components/MobilePageBar';
import PublishDashboard from './PublishDashboard';
import ComposeBatchRail from '../components/project/ComposeBatchRail';
import CreateMediaModal from '../components/CreateMediaModal';
import PublishAccountChip from '../components/PublishAccountChip';
import { usePublishAccount } from '../hooks/usePublishAccount';
import { uploadToR2 } from '../utils/r2Upload';

const ListIcon = FiList as unknown as React.FC<{ 'aria-hidden'?: boolean }>;

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface Project {
  id: string;
  name: string;
  createdAt: string;
}

type ItemKind = 'story' | 'caption' | 'shorts';

interface ProjectItem {
  kind: ItemKind;
  id: string;
  title: string;
  status: string;
  publishStatus?: string;
  latestRenderUrl: string | null;
  updatedAt?: string | null;
}

/** Time of day for something from today, otherwise the full date and time. */
function stamp(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (d.toDateString() === new Date().toDateString()) return time;
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}, ${time}`;
}

const KIND_TAG: Record<ItemKind, { label: string; glyph: string }> = {
  story: { label: 'Story', glyph: '📖' },
  caption: { label: 'Caption', glyph: '💬' },
  shorts: { label: 'Shorts', glyph: '🎬' },
};

interface DraftDetail {
  id: string;
  video_id: string;
  project_id: string | null;
  title: string | null;
}

interface UploadInitResponse {
  videoId: string;
  presignedUrl: string;
}

const PostsCompose: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const draftId = searchParams.get('draftId');
  const isDraftMode = Boolean(draftId);
  const queryProjectId = searchParams.get('projectId');
  const queryVideoId = searchParams.get('videoId');
  const queryShortId = searchParams.get('shortId');
  const queryKind = (searchParams.get('kind') || 'shorts') as 'shorts' | 'caption' | 'story';
  const publishAccount = usePublishAccount(queryProjectId);
  const queryItemId = searchParams.get('itemId');
  const composeSourceId = queryKind === 'shorts' ? queryVideoId : queryItemId;
  const hasDirectComposeTarget = Boolean(queryProjectId && composeSourceId);

  const [projects, setProjects] = useState<Project[]>([]);
  const [items, setItems] = useState<ProjectItem[]>([]);
  // For caption/story compose targets the real videoId is resolved by the batch rail.
  const [composerVideoId, setComposerVideoId] = useState<string | null>(null);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  // Below 900px the batch rail is a left-hand overlay drawer (see responsive.css).
  const [batchOpen, setBatchOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);

  const [draft, setDraft] = useState<DraftDetail | null>(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const requiresDraftLookup = isDraftMode && !hasDirectComposeTarget;

  useEffect(() => {
    if (isDraftMode || hasDirectComposeTarget) {
      return;
    }

    setLoadingProjects(true);
    fetch(`${API_BASE}/api/projects`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then((projectsData) => {
        setProjects(Array.isArray(projectsData) ? projectsData : []);
      })
      .catch(() => {
        setProjects([]);
      })
      .finally(() => {
        setLoadingProjects(false);
      });
  }, [isDraftMode, hasDirectComposeTarget]);

  useEffect(() => {
    if (isDraftMode || hasDirectComposeTarget) {
      return;
    }

    if (!selectedProject) {
      setItems([]);
      return;
    }

    setLoadingItems(true);
    fetch(`${API_BASE}/api/projects/${encodeURIComponent(selectedProject)}/items`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then((itemsData) => {
        setItems(Array.isArray(itemsData) ? itemsData : []);
      })
      .catch(() => {
        setItems([]);
      })
      .finally(() => {
        setLoadingItems(false);
      });
  }, [hasDirectComposeTarget, isDraftMode, selectedProject]);

  const loadDraft = useCallback(async () => {
    if (!draftId) {
      return;
    }

    setDraftLoading(true);
    setDraftError(null);
    try {
      const response = await fetch(`${API_BASE}/api/posts/drafts/${encodeURIComponent(draftId)}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({} as { error?: string }));
        throw new Error(payload.error || 'Failed to load draft');
      }

      const payload = await response.json() as DraftDetail;
      setDraft(payload);
    } catch (err: unknown) {
      setDraftError(err instanceof Error ? err.message : 'Failed to load draft');
      setDraft(null);
    } finally {
      setDraftLoading(false);
    }
  }, [draftId]);

  useEffect(() => {
    if (!requiresDraftLookup) {
      return;
    }
    void loadDraft();
  }, [requiresDraftLookup, loadDraft]);

  const handleUploadVideo = useCallback(async (
    file: File,
    name: string,
    description: string,
    language: string,
    whisperModel: string
  ) => {
    if (!selectedProject) {
      setUploadError('Select a project first. Project selection is required before upload.');
      return;
    }

    setCreateModalOpen(false);
    setUploading(true);
    setUploadError(null);
    setUploadMessage('Initializing upload...');

    try {
      const initRes = await fetch(`${API_BASE}/api/projects/${encodeURIComponent(selectedProject)}/videos/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          fileName: file.name,
          fileSize: file.size,
          name,
          description,
          language,
          whisperModel,
        }),
      });

      if (!initRes.ok) {
        const data = await initRes.json().catch(() => ({ error: 'Failed to initialize upload' }));
        throw new Error(data.error || 'Failed to initialize upload');
      }

      const initPayload = await initRes.json() as UploadInitResponse;

      setUploadMessage('Uploading video...');
      await uploadToR2(initPayload.presignedUrl, file);

      // Publish is keyed on a short, and this flow runs no pipeline — make the raw file one.
      setUploadMessage('Preparing video...');
      const shortRes = await fetch(
        `${API_BASE}/api/projects/${encodeURIComponent(selectedProject)}/videos/${encodeURIComponent(initPayload.videoId)}/source-short`,
        { method: 'POST', credentials: 'include' }
      );
      if (!shortRes.ok) {
        const data = await shortRes.json().catch(() => ({ error: 'Failed to prepare video' }));
        throw new Error(data.error || 'Failed to prepare video');
      }

      // +Post flow: skip media-pipeline confirm/poll and open compose immediately.
      navigate(`/posts/compose?projectId=${encodeURIComponent(selectedProject)}&videoId=${encodeURIComponent(initPayload.videoId)}`);
    } catch (err: unknown) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      setUploadMessage(null);
    }
  }, [navigate, selectedProject]);

  const handleFromImages = useCallback((name: string, description: string) => {
    if (!selectedProject) {
      setUploadError('Select a project first. Project selection is required before upload.');
      return;
    }

    setCreateModalOpen(false);

    const params = new URLSearchParams();
    if (name) params.set('name', name);
    if (description) params.set('description', description);
    const queryString = params.toString();

    navigate(`/projects/${encodeURIComponent(selectedProject)}/image-video${queryString ? `?${queryString}` : ''}`);
  }, [navigate, selectedProject]);

  useEffect(() => { setComposerVideoId(null); }, [composeSourceId, queryKind]);

  useEffect(() => {
    if (!batchOpen) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setBatchOpen(false); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [batchOpen]);

  const selectShort = useCallback((shortId: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('shortId', shortId);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const resolvedProjectId = queryProjectId || draft?.project_id || null;
  const resolvedVideoId = queryVideoId || draft?.video_id || null;

  // Editing an existing draft — keep the single-composer full-page view.
  if (isDraftMode && resolvedProjectId && resolvedVideoId) {
    return (
      <PublishDashboard
        projectIdOverride={resolvedProjectId}
        videoIdOverride={resolvedVideoId}
        draftIdOverride={draftId || undefined}
        breadcrumbMode="compose"
      />
    );
  }

  // Composing for a chosen project item — 3-column layout: batch rail + composer.
  if (hasDirectComposeTarget && queryProjectId && composeSourceId) {
    const composerVid = queryKind === 'shorts' ? queryVideoId : composerVideoId;
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <main className="main-content">
          <Header />
          <div className="breadcrumb">
            <div className="breadcrumb-links">
              <BreadcrumbBack to="/posts/compose" />
              <Link to="/posts">Posts</Link>
              <span className="breadcrumb-sep">›</span>
              <Link to="/posts/compose">Compose</Link>
              <span className="breadcrumb-sep">›</span>
              <span>Compose post</span>
            </div>
          </div>
          <div className="page-header compose-page-header">
            <div>
              <h1>Compose Post</h1>
              <p className="page-subtitle">
                <span className="compose-copy-wide">Pick a batch and video on the left, then publish, schedule or save a draft.</span>
                <span className="compose-copy-narrow">Pick a video, then publish, schedule or save a draft.</span>
              </p>
            </div>
            <div className="compose-header-actions">
              <span className="compose-label">✍ Compose</span>
              <Link
                to="/posts/compose"
                className="btn-secondary compose-change-btn"
                title="Change video"
                aria-label="Change video"
              >
                <span aria-hidden="true">←</span>
                <span className="compose-btn-text"> Change video</span>
              </Link>
              <PublishAccountChip pa={publishAccount} />
              <button
                type="button"
                className="btn-secondary compose-batch-btn"
                onClick={() => setBatchOpen(true)}
                aria-expanded={batchOpen}
                title="Show batches"
                aria-label="Show batches"
              >
                <ListIcon aria-hidden={true} />
                <span className="compose-btn-text">Batch</span>
              </button>
            </div>
          </div>
          <div className="compose-3col">
            <div className={`cbr-drawer${batchOpen ? ' cbr-drawer--open' : ''}`}>
              <div className="cbr-drawer-head">
                <span className="cbr-drawer-title">Batch</span>
                <button type="button" className="cbr-drawer-close" onClick={() => setBatchOpen(false)} aria-label="Close batch list">✕</button>
              </div>
              <ComposeBatchRail
                key={`${queryKind}-${composeSourceId}`}
                kind={queryKind}
                sourceId={composeSourceId}
                projectId={queryProjectId}
                selectedShortId={queryShortId || undefined}
                publishAccount={publishAccount}
                onSelectShort={(shortId) => { selectShort(shortId); setBatchOpen(false); }}
                onResolveVideoId={queryKind === 'shorts' ? undefined : setComposerVideoId}
              />
            </div>
            <div className={`cbr-backdrop${batchOpen ? ' is-open' : ''}`} onClick={() => setBatchOpen(false)} aria-hidden="true" />
            {composerVid ? (
              <PublishDashboard
                embedded
                projectIdOverride={queryProjectId}
                videoIdOverride={composerVid}
                selectedShortId={queryShortId || undefined}
                publishAccount={publishAccount}
                breadcrumbMode="compose"
              />
            ) : (
              <div className="pub-embed"><div className="project-loading">Loading…</div></div>
            )}
          </div>
        </main>
      </div>
    );
  }

  if (requiresDraftLookup && draftLoading) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <main className="main-content">
          <Header />
          <div className="project-loading">Loading draft...</div>
        </main>
      </div>
    );
  }

  if (isDraftMode) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <main className="main-content">
          <Header />
          <div className="project-empty">
            <h3>Unable to open this draft in Compose</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>
              {draftError || 'This draft is missing project or video information.'}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button className="btn-secondary" onClick={() => void loadDraft()}>Retry</button>
              <Link to="/posts/drafts" className="btn-primary">Back to Drafts</Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="main-content">
        <Header />

        <CreateMediaModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onUploadVideo={handleUploadVideo}
          onFromImages={handleFromImages}
          onFromLink={() => {}}
          uploading={uploading}
          uploadOnly
        />

        <MobilePageBar current="Compose" label="✍ Compose" />

        <div className="page-header posts-list-header">
          <div>
            <h1>Compose Post</h1>
            <p className="page-subtitle">Select a project, then choose or upload a video to compose and publish</p>
          </div>
        </div>

        {(uploadError || uploadMessage) && (
          <div style={{ marginBottom: 14 }}>
            {uploadError && <div className="error-banner">⚠️ {uploadError}</div>}
            {!uploadError && uploadMessage && (
              <div className="project-loading" style={{ padding: '10px 14px' }}>{uploadMessage}</div>
            )}
          </div>
        )}

        <div className="compose-project-row" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 14, color: 'var(--text-secondary)' }}>Project (required):</label>
          <select
            className="compose-project-select"
            value={selectedProject}
            onChange={e => {
              setSelectedProject(e.target.value);
              setUploadError(null);
            }}
            style={{
              padding: '8px 12px',
              background: 'var(--bg-input)',
              border: '1px solid var(--border-secondary)',
              borderRadius: 8,
              color: 'var(--text-primary)',
              fontSize: 14,
              cursor: 'pointer',
              minWidth: 220,
            }}
          >
            <option value="">Select a project</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>{project.name}</option>
            ))}
          </select>

          <button
            className="btn-primary compose-publish-btn"
            onClick={() => setCreateModalOpen(true)}
            disabled={!selectedProject || loadingItems || uploading}
            title={!selectedProject ? 'Select a project first' : 'Upload a video and publish'}
          >
            {uploading ? '⏳ Uploading...' : '+ Publish'}
          </button>
        </div>

        {loadingProjects ? (
          <div style={{ padding: '60px 0', textAlign: 'center', color: 'var(--text-secondary)' }}>
            Loading projects...
          </div>
        ) : projects.length === 0 ? (
          <div style={{ padding: '60px 0', textAlign: 'center', color: 'var(--text-secondary)' }}>
            <p>No projects available.</p>
            <Link to="/projects" className="btn-primary" style={{ marginTop: 20, display: 'inline-block' }}>
              Create a Project
            </Link>
          </div>
        ) : !selectedProject ? (
          <div className="compose-empty">
            <p>Select a project to continue. Project selection is required before upload and compose.</p>
          </div>
        ) : loadingItems ? (
          <div style={{ padding: '60px 0', textAlign: 'center', color: 'var(--text-secondary)' }}>
            Loading project items...
          </div>
        ) : items.length === 0 ? (
          <div style={{ padding: '60px 0', textAlign: 'center', color: 'var(--text-secondary)' }}>
            <p>Nothing in this project yet.</p>
            <p style={{ marginTop: 8 }}>Use + Publish to upload a new video to this project.</p>
          </div>
        ) : (
          <div className="content-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {items.map(item => {
              const tag = KIND_TAG[item.kind];
              const to = item.kind === 'shorts'
                ? `/posts/compose?projectId=${encodeURIComponent(selectedProject)}&videoId=${encodeURIComponent(item.id)}`
                : `/posts/compose?projectId=${encodeURIComponent(selectedProject)}&kind=${item.kind}&itemId=${encodeURIComponent(item.id)}`;
              const hasRender = Boolean(item.latestRenderUrl);
              return (
                <Link
                  key={`${item.kind}-${item.id}`}
                  to={to}
                  className="video-card"
                  style={{ textDecoration: 'none', opacity: hasRender ? 1 : 0.6 }}
                  title={hasRender ? 'Compose and publish' : 'No finished render yet'}
                >
                  <div className="video-card-header">
                    <span className={`pi-badge pi-badge--${item.kind}`}>{tag.glyph} {tag.label}</span>
                    {item.publishStatus === 'published' ? (
                      <span className="status-badge status-done">published</span>
                    ) : (
                      <span className={`status-badge ${item.status === 'completed' ? 'status-done' : ''}`}>
                        {item.status}
                      </span>
                    )}
                  </div>
                  <div className="video-card-body">
                    <h3>{item.title || 'Untitled'}</h3>
                    <p className="video-meta">
                      <span>{hasRender ? 'Compose →' : 'Render first →'}</span>
                      {stamp(item.updatedAt) && <span> · {stamp(item.updatedAt)}</span>}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default PostsCompose;
